import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCountry } from "../contexts/CountryContext";
import { API_URL } from "../lib/supabase";
import { getServiceGradient } from "../lib/constants";
import { ServiceIcon } from "../components/shared/ServiceIcon";
import { Star, Shield, ChevronRight, User } from "lucide-react";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { countryCode } = useCountry();
  const [service, setService] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, countryCode]);

  const fetchServiceData = async () => {
    setLoading(true);
    try {
      const serviceRes = await fetch(`${API_URL}/services/${slug}`);
      const serviceData = await serviceRes.json();
      setService(serviceData);

      // Fetch providers for this service in the active country only
      const providersRes = await fetch(`${API_URL}/providers?service_id=${serviceData.id}&country_code=${encodeURIComponent(countryCode)}`);
      const providersData = await providersRes.json();
      setProviders(providersData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestQuote = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=' + encodeURIComponent(`/quote-request/${slug}`));
    } else {
      navigate(`/quote-request/${slug}`);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!service) return <div className="error">Service not found</div>;

  return (
    <div className="page service-detail-page">
      <div className="breadcrumb">
        <Link to="/services">Services</Link>
        <ChevronRight size={16} />
        {service.category && (
          <>
            <Link to={`/categories/${service.category.slug}`}>{service.category.name}</Link>
            <ChevronRight size={16} />
          </>
        )}
        <span>{service.name}</span>
      </div>

      <div className="service-detail-grid">
        <div className="service-detail-main">
          <div className={`svc-detail-hero ${getServiceGradient(service)}`}>
            <ServiceIcon slug={service.slug} size={40} />
            {service.category && (
              <span className="svc-detail-cat">{service.category.name}</span>
            )}
          </div>
          <h1>{service.name}</h1>
          <div className="service-detail-price">{service.price_display}</div>
          <p className="service-detail-description">{service.long_description || service.short_description}</p>
          
          <button 
            onClick={handleRequestQuote} 
            className="btn-primary btn-large"
            data-testid="request-quote-button"
          >
            Request a Quote
          </button>
        </div>

        <div className="service-detail-sidebar">
          <h3>Available Providers</h3>
          {providers.length > 0 ? (
            <div className="provider-list">
              {providers.map(provider => (
                <Link 
                  to={`/providers/${provider.id}`} 
                  key={provider.id} 
                  className="provider-mini-card"
                  data-testid={`provider-${provider.id}`}
                >
                  <div className="provider-avatar">
                    {provider.profile?.avatar_url ? (
                      <img src={provider.profile.avatar_url} alt={provider.business_name} />
                    ) : (
                      <User size={24} />
                    )}
                  </div>
                  <div className="provider-info">
                    <h4>{provider.business_name}</h4>
                    <div className="provider-rating">
                      <Star size={14} fill="currentColor" />
                      <span>{provider.average_rating?.toFixed(1) || 'New'}</span>
                      <span className="review-count">({provider.review_count} reviews)</span>
                    </div>
                  </div>
                  {provider.is_id_verified && (
                    <Shield size={16} className="verified-badge" />
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <p className="no-providers">No providers available yet. Request a quote and we'll notify matching professionals.</p>
          )}
        </div>
      </div>
    </div>
  );
};


export default ServiceDetailPage;
